import api from '@/api';

export default {
    namespaced: true,
    state: {
        chapterSubjects: [],
        currentChapterSubject: null,
        isLoading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: 10,
            from: 0,
            to: 0
        },
        filters: {
            search: '',
            status: '',
            chapter_id: null,
            subject_id: null
        }
    },
    mutations: {
        SET_CHAPTER_SUBJECTS(state, chapterSubjects) {
            state.chapterSubjects = chapterSubjects;
        },
        SET_CURRENT_CHAPTER_SUBJECT(state, chapterSubject) {
            state.currentChapterSubject = chapterSubject;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = pagination;
        },
        SET_FILTERS(state, filters) {
            state.filters = filters;
        },
        ADD_CHAPTER_SUBJECT(state, chapterSubject) {
            state.chapterSubjects.unshift(chapterSubject);
        },
        UPDATE_CHAPTER_SUBJECT(state, updatedChapterSubject) {
            const index = state.chapterSubjects.findIndex(cs => cs.id === updatedChapterSubject.id);
            if (index !== -1) state.chapterSubjects.splice(index, 1, updatedChapterSubject);
        },
        DELETE_CHAPTER_SUBJECT(state, chapterSubjectId) {
            state.chapterSubjects = state.chapterSubjects.filter(cs => cs.id !== chapterSubjectId);
        }
    },
    actions: {
        async fetchChapterSubjects({ commit, state }, { page = 1, per_page = 10 } = {}) {
            commit('SET_LOADING', true);
            try {
                const response = await api.get('/chapter-subjects', {
                    params: {
                        page: page || state.pagination.currentPage,
                        per_page: per_page || state.pagination.perPage,
                        search: state.filters.search,
                        status: state.filters.status,
                        chapter_id: state.filters.chapter_id,
                        subject_id: state.filters.subject_id,
                        with: 'chapter,subject'
                    }
                });
                
                commit('SET_CHAPTER_SUBJECTS', response.data.data);
                commit('SET_PAGINATION', {
                    currentPage: response.data.meta.current_page,
                    totalPages: response.data.meta.last_page,
                    totalItems: response.data.meta.total,
                    perPage: response.data.meta.per_page,
                    from: response.data.meta.from,
                    to: response.data.meta.to
                });
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async fetchChapterSubject({ commit }, id) {
            commit('SET_LOADING', true);
            try {
                const response = await api.get(`/chapter-subjects/${id}`, {
                    params: { with: 'chapter,subject' }
                });
                commit('SET_CURRENT_CHAPTER_SUBJECT', response.data.data);
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async createChapterSubject({ commit }, formData) {
            commit('SET_LOADING', true);
            try {
                const response = await api.post('/chapter-subjects', formData);
                commit('ADD_CHAPTER_SUBJECT', response.data.data);
                return response.data;
            } catch (error) {
                if (error.response?.status === 422) {
                    throw error.response.data.errors;
                }
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async updateChapterSubject({ commit }, { id, chapterSubjectData }) {
            commit('SET_LOADING', true);
            try {
                const response = await api.put(`/chapter-subjects/${id}`, chapterSubjectData);
                commit('UPDATE_CHAPTER_SUBJECT', response.data.data);
                return response.data;
            } catch (error) {
                if (error.response?.status === 422) {
                    throw error;
                }
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        async deleteChapterSubject({ commit }, chapterSubjectId) {
            commit('SET_LOADING', true);
            try {
                await api.delete(`/chapter-subjects/${chapterSubjectId}`);
                commit('DELETE_CHAPTER_SUBJECT', chapterSubjectId);
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },

        setFilters({ commit, dispatch }, filters) {
            commit('SET_FILTERS', filters);
            dispatch('fetchChapterSubjects');
        }
    },
    getters: {
        chapterSubjects: state => state.chapterSubjects,
        chapterSubject: state => state.currentChapterSubject,
        isLoading: state => state.isLoading,
        error: state => state.error,
        pagination: state => state.pagination
    }
};